import LayerRenderer from './layer-renderer';
import RenderUtil from './render-util';

// area layers ARE the geometry - plain field names instead of the ref* params of inline refRects
const AREA_FIELDS = { x: 'x', y: 'y', width: 'width', height: 'height', angle: 'angle' };

// display defaults (docs/layer-area.md section 9)
const DISPLAY_BORDER_COLOR = '#139cf6';
const DISPLAY_BORDER_WIDTH = 2;

/*
  NEW: area layer - named, condition-resolved reference geometry (docs/layer-area.md)
  consumer layers reference an area by key via params.refArea - resolution happens in product-preview

  - renders nothing on the storefront by default (pure geometry)
  - params.display draws a styled guide box on the storefront (section 9) - cut lines, safe margins,
    "keep out" zones - purely decorative, never affects geometry resolution
  - params.displayType 'i' (interactive) pre-renders the guide hidden - product-preview shows it
    while a consumer layer of this area is dragged/scaled/rotated (no re-render involved)
  - in editor mode it renders the same editable reference box as inline refRects
    (drag/resize/rotate updates params.x/y/width/height/angle via the object:modified _layerRef handler),
    plus the display decorations (label + center lines) as static, always-visible objects
*/
class AreaRenderer extends LayerRenderer {

    render(layer, context, canvas){

        // editorMode editable area box && NOT cropMode (same visibility rules as _addRefRect)
        if(RenderUtil.isEditLayer(layer, context.editorMode, context.editLayer) && !context.cropMode){

            // same editor box as inline refRects - only the geometry field names differ
            let areaRect = this._getRefRect(layer, context, AREA_FIELDS);

            // the editor box adopts the configured display style so the merchant edits the box
            // looking the way it renders on the storefront (blue default when display is unset)
            this._applyDisplayStyle(areaRect, layer);

            canvas.add(areaRect);

            // NEW: the display decorations (label + center lines) render statically with the editor
            // box so the merchant sees what is configured - ALWAYS visible while editing, in
            // particular for displayType 'i' (the storefront only shows those during a customer
            // drag). static per render: they do not follow the live box drag, the object:modified
            // re-render snaps them to the updated geometry. evented:false keeps the box drag
            // surface clear (they sit on top without blocking selection)
            if(layer.params.display){
                this._getDecorations(layer, context, this._baseOptions(layer, true)).forEach((object) => canvas.add(object));
            }

            // NEW: an area linked to a dimension option shows the box fitted to the entered size as a dashed outline
            // inside the editable box, so the merchant sees it follow the option (re-rendered on each option change)
            if(layer._linkedParams){
                canvas.add(this._linkedOutline(layer, context));
            }

            // same as _addRefRect: allow scaling not in ratio while editing the box
            canvas.uniformScaling = false;

            // allow editor layer selection (selectLayer selects layer._object)
            layer._object = areaRect;
        }
        // NEW: storefront guide box (docs/layer-area.md section 9)
        // z-order comes from the layer order - the guide is added in place like any rendered layer
        else if(layer.params.display){
            this._getDisplayObjects(layer, context).forEach((object) => canvas.add(object));
        }

        return Promise.resolve();
    }

    /*
      fabric style options derived from the display params - shared by the storefront guide box
      and the editor box (the merchant previews exactly what the storefront renders)
      cornerRadius is display only: consumer geometry (clipPath/containment/crop) stays rectangular
    */
    _displayStyle(params){

        let borderWidth = typeof params.borderWidth !== 'undefined' ? params.borderWidth : DISPLAY_BORDER_WIDTH;

        let style = {
            fill: params.fill || 'transparent',
            opacity: 1,
            stroke: params.borderColor || DISPLAY_BORDER_COLOR,
            strokeWidth: borderWidth,
            strokeDashArray: null,
            rx: params.cornerRadius || 0,
            ry: params.cornerRadius || 0
        };

        // border style maps to a dash pattern relative to the border width
        if(params.borderStyle === 'dashed'){
            style.strokeDashArray = [borderWidth * 4, borderWidth * 2];
        } else if(params.borderStyle === 'dotted'){
            style.strokeDashArray = [borderWidth, borderWidth * 2];
        }

        return style;
    }

    _applyDisplayStyle(areaRect, layer){
        if(layer.params.display){
            areaRect.setOptions(this._displayStyle(layer.params));
        } else {
            // reset a cached box back to the default blue editor styling (display toggled off mid session)
            // fill/opacity defaults are re-applied by _getRefRect on every call
            areaRect.setOptions({ stroke: null, strokeWidth: 0, strokeDashArray: null, rx: 0, ry: 0 });
        }
    }

    // same geometry defaults as the editor box (_getRefRect)
    // NEW: the storefront guide follows a box linked to a dimension option (RenderUtil.linkedAreaParams) - the editor box and its
    // decorations stay on the configured box the merchant edits
    _getGeometry(layer, context){
        let editing = RenderUtil.isEditLayer(layer, context.editorMode, context.editLayer) && !context.cropMode;
        return RenderUtil.areaBox(editing ? layer.params : RenderUtil.areaParams(layer), context.config);
    }

    // the editor outline of the linked box (areaParams) - static per render like the decorations, evented:false
    _linkedOutline(layer, context){
        let geometry = RenderUtil.areaBox(layer._linkedParams, context.config);
        let stroke = (layer.params.display && layer.params.borderColor) || DISPLAY_BORDER_COLOR;
        return new fabric.Rect(Object.assign({
            left: geometry.x,
            top: geometry.y,
            width: geometry.width,
            height: geometry.height,
            scaleX: 1,
            scaleY: 1,
            angle: geometry.angle,
            fill: 'transparent',
            stroke: stroke,
            strokeWidth: DISPLAY_BORDER_WIDTH,
            strokeDashArray: [DISPLAY_BORDER_WIDTH * 4, DISPLAY_BORDER_WIDTH * 2],
            strokeUniform: true
        }, this._baseOptions(layer, true)));
    }

    // shared non-interactive object options - clicks and drags pass through to the layers below
    _baseOptions(layer, visible){
        return {
            visible: visible,
            selectable: false,
            evented: false,
            hasControls: false,
            _layer: layer // export pipelines read the exclude flags from the layer params
        };
    }

    /*
      NEW: storefront guide box (docs/layer-area.md section 9)
      rect + decorations (label, center lines) - purely decorative:
      - non-interactive: clicks and drags pass through to the content layers below
      - _layer is set so the existing export flags apply (excludeOriginal/excludeThumbnail/exportOnly)
      - never dirty (no _interactive flags), so exportState/restore ignore it
    */
    _getDisplayObjects(layer, context){

        let params = layer.params;

        let geometry = this._getGeometry(layer, context);

        let visible = layer.hasOwnProperty('_visible') ? layer._visible : true; // exportOnly support (set in _renderLayer)

        // interactive guides start hidden - product-preview shows them while a consumer is dragged
        if(params.displayType === 'i'){
            visible = false;
        }

        let baseOptions = this._baseOptions(layer, visible);

        let rect = new fabric.Rect(Object.assign({
            left: geometry.x,
            top: geometry.y,
            width: geometry.width,
            height: geometry.height,
            scaleX: 1,
            scaleY: 1,
            angle: geometry.angle
        }, this._displayStyle(params), baseOptions));

        let objects = [rect].concat(this._getDecorations(layer, context, baseOptions));

        // store refs for the interaction driven visibility toggle (displayType 'i',
        // product-preview._syncAreaGuide) - replaced on every render pass
        layer._displayObjects = objects;

        return objects;
    }

    /*
      display decorations (label + center lines) shared by the storefront guide box and the
      editor box (rendered statically there so the merchant sees what is configured)
    */
    _getDecorations(layer, context, baseOptions){

        let params = layer.params;
        let { x, y, width, height, angle } = this._getGeometry(layer, context);
        let style = this._displayStyle(params);

        let objects = [];

        // optional label at the top edge ("Cut line", "Safe area")
        if(params.label){
            // labels scale with the preview canvas (configs are sized in canvas px, not screen px)
            let fontSize = Math.max(14, Math.round(Math.min(context.config.width, context.config.height) / 40));
            let padding = style.strokeWidth + Math.round(fontSize / 3);

            let label = new fabric.Text(String(params.label), Object.assign({
                fontFamily: 'Helvetica, Arial, sans-serif',
                fontSize: fontSize,
                fill: style.stroke
            }, baseOptions));

            this._positionLabel(label, x + padding, y + padding, x, y, angle);
            objects.push(label);
        }

        // NEW: center lines (docs/layer-area.md section 9) - dashed lines through the area center,
        // shown/hidden with the guide box (interactive guides: while a consumer is dragged) and the
        // visual anchor for the optional consumer center snap (params.snap on the area layer,
        // product-preview object:moving handler). per axis, naming matches autoCenterH/V:
        // displayCenterH = the horizontal-centering anchor = the VERTICAL line (and vice versa)
        if(params.displayCenterH || params.displayCenterV){

            // always dashed so the lines read as guides, not as content - borderWidth 0 hides the
            // box border but must not hide the requested center lines
            let lineWidth = style.strokeWidth || DISPLAY_BORDER_WIDTH;

            let lineOptions = Object.assign({
                stroke: style.stroke,
                strokeWidth: lineWidth,
                strokeDashArray: [lineWidth * 4, lineWidth * 2]
            }, baseOptions);

            if(params.displayCenterH){
                objects.push(this._centerLine(x + width / 2, y, x + width / 2, y + height, x, y, angle, lineOptions));
            }
            if(params.displayCenterV){
                objects.push(this._centerLine(x, y + height / 2, x + width, y + height / 2, x, y, angle, lineOptions));
            }
        }

        return objects;
    }

    // center line endpoints rotate with the box (around its left/top origin, like the label) -
    // the line cross stays at the area center, matching the snap target for any angle
    _centerLine(x1, y1, x2, y2, originX, originY, angle, options){
        if(angle){
            let origin = new fabric.Point(originX, originY);
            let radians = fabric.util.degreesToRadians(angle);
            let p1 = fabric.util.rotatePoint(new fabric.Point(x1, y1), origin, radians);
            let p2 = fabric.util.rotatePoint(new fabric.Point(x2, y2), origin, radians);
            return new fabric.Line([p1.x, p1.y, p2.x, p2.y], options);
        }
        return new fabric.Line([x1, y1, x2, y2], options);
    }

    // rotate the unrotated label position with the box (the box rotates around its left/top origin)
    _positionLabel(label, left, top, originX, originY, angle){
        if(angle){
            let point = fabric.util.rotatePoint(new fabric.Point(left, top), new fabric.Point(originX, originY), fabric.util.degreesToRadians(angle));
            label.set({ left: point.x, top: point.y, angle: angle });
        } else {
            label.set({ left: left, top: top });
        }
    }

}

export default AreaRenderer;
